import React, { useRef, useState } from "react";

export type UploadedSeries = {
  id: string;
  fileName: string;
  entity: string;
  date: string;
  rows: ParsedRow[];
  visible: boolean;
  errors: string[];
};

type ParsedRow = {
  entity: string;
  date: string;
  block: number;
  time: string;
  forecast_mw: number;
};

const REQUIRED_COLUMNS = ["entity", "date", "block", "time", "forecast_mw"];
const TIME_SLOTS = Array.from({ length: 96 }, (_, i) => {
  const totalMinutes = i * 15;
  const h = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
  const m = String(totalMinutes % 60).padStart(2, "0");
  return `${h}:${m}`;
});

function generateTemplate(entity = "ENTITY", date = "2026-01-01"): string {
  const header = "entity,date,block,time,forecast_mw";
  const rows = TIME_SLOTS.map((t, i) => `${entity},${date},${i + 1},${t},`);
  return [header, ...rows].join("\n");
}

function downloadCsv(content: string, fileName: string) {
  const blob = new Blob([content], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

function parseAndValidate(text: string, fileName: string): UploadedSeries {
  const lines = text.trim().split(/\r?\n/);
  const errors: string[] = [];

  const fileNamePattern =
    /^adjusted_forecast_([A-Za-z0-9_]+)_(\d{4}-\d{2}-\d{2})\.csv$/;
  const fileNameMatch = fileName.match(fileNamePattern);
  if (!fileNameMatch) {
    errors.push(
      `File name "${fileName}" does not match required format: adjusted_forecast_{ENTITY}_{YYYY-MM-DD}.csv`,
    );
  }
  const fileNameEntity = fileNameMatch?.[1] ?? "";
  const fileNameDate = fileNameMatch?.[2] ?? "";

  if (lines.length < 2) {
    return {
      id: crypto.randomUUID(),
      fileName,
      entity: "",
      date: "",
      rows: [],
      visible: true,
      errors: ["File is empty or has no data rows."],
    };
  }

  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());

  const missing = REQUIRED_COLUMNS.filter((c) => !headers.includes(c));
  if (missing.length > 0) {
    return {
      id: crypto.randomUUID(),
      fileName,
      entity: "",
      date: "",
      rows: [],
      visible: true,
      errors: [`Missing columns: ${missing.join(", ")}`],
    };
  }

  const idx = (col: string) => headers.indexOf(col);

  const dataLines = lines.slice(1).filter((l) => l.trim() !== "");

  if (dataLines.length !== 96) {
    errors.push(
      `Expected 96 data rows (one per 15-min block), got ${dataLines.length}.`,
    );
  }

  const rows: ParsedRow[] = [];

  dataLines.forEach((line, i) => {
    const cols = line.split(",");
    const rowNum = i + 2;

    const entity = cols[idx("entity")]?.trim() ?? "";
    const date = cols[idx("date")]?.trim() ?? "";
    const blockRaw = cols[idx("block")]?.trim() ?? "";
    const time = cols[idx("time")]?.trim() ?? "";
    const fmwRaw = cols[idx("forecast_mw")]?.trim() ?? "";

    if (!entity) errors.push(`Row ${rowNum}: entity is empty.`);
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date))
      errors.push(
        `Row ${rowNum}: invalid date "${date}" (expected YYYY-MM-DD).`,
      );

    const block = Number(blockRaw);
    if (!blockRaw || isNaN(block) || block < 1 || block > 96)
      errors.push(`Row ${rowNum}: invalid block "${blockRaw}" (must be 1–96).`);

    if (!time || !/^\d{2}:\d{2}$/.test(time))
      errors.push(`Row ${rowNum}: invalid time "${time}" (expected HH:MM).`);

    if (!fmwRaw || fmwRaw === "") {
      errors.push(`Row ${rowNum}: forecast_mw is empty.`);
    } else {
      const fmw = Number(fmwRaw);
      if (isNaN(fmw))
        errors.push(`Row ${rowNum}: forecast_mw "${fmwRaw}" is not a number.`);
      else if (fmw < 0)
        errors.push(`Row ${rowNum}: forecast_mw cannot be negative.`);
      else rows.push({ entity, date, block, time, forecast_mw: fmw });
    }
  });

  const uniqueEntities = [...new Set(rows.map((r) => r.entity))];
  const uniqueDates = [...new Set(rows.map((r) => r.date))];

  if (uniqueEntities.length > 1)
    errors.push(
      `Multiple entities found: ${uniqueEntities.join(", ")}. Use one entity per file.`,
    );
  if (uniqueDates.length > 1)
    errors.push(
      `Multiple dates found: ${uniqueDates.join(", ")}. Use one date per file.`,
    );

  if (
    fileNameEntity &&
    uniqueEntities.length === 1 &&
    uniqueEntities[0] !== fileNameEntity
  )
    errors.push(
      `Entity in data ("${uniqueEntities[0]}") does not match file name entity ("${fileNameEntity}").`,
    );
  if (
    fileNameDate &&
    uniqueDates.length === 1 &&
    uniqueDates[0] !== fileNameDate
  )
    errors.push(
      `Date in data ("${uniqueDates[0]}") does not match file name date ("${fileNameDate}").`,
    );

  return {
    id: crypto.randomUUID(),
    fileName,
    entity: uniqueEntities[0] ?? "",
    date: uniqueDates[0] ?? "",
    rows,
    visible: true,
    errors,
  };
}

type Props = {
  series: UploadedSeries[];
  onChange: (series: UploadedSeries[]) => void;
  defaultEntity?: string;
  buttonsOnly?: boolean;
};

export function UploadCustomSeries({
  series,
  onChange,
  defaultEntity,
  buttonsOnly,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [expandedErrors, setExpandedErrors] = useState<string | null>(null);

  function handleDownloadTemplate() {
    const today = new Date().toISOString().split("T")[0];
    const entity =
      defaultEntity && defaultEntity.length > 0 ? defaultEntity : "ENTITY";
    downloadCsv(
      generateTemplate(entity, today),
      `adjusted_forecast_${entity}_${today}.csv`,
    );
  }

  function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;

    const readers = files.map(
      (file) =>
        new Promise<UploadedSeries>((resolve) => {
          const reader = new FileReader();
          reader.onload = (ev) => {
            const text = ev.target?.result as string;
            resolve(parseAndValidate(text, file.name));
          };
          reader.readAsText(file);
        }),
    );

    Promise.all(readers).then((parsed) => {
      onChange([...series, ...parsed]);
    });

    e.target.value = "";
  }

  function toggleVisible(id: string) {
    onChange(
      series.map((s) => (s.id === id ? { ...s, visible: !s.visible } : s)),
    );
  }

  function removeSeries(id: string) {
    onChange(series.filter((s) => s.id !== id));
  }

  if (buttonsOnly) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <button
          type="button"
          onClick={handleDownloadTemplate}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontFamily: "inherit",
            fontSize: 12,
            fontWeight: 500,
            padding: "6px 12px",
            borderRadius: 6,
            cursor: "pointer",
            border: "1.5px solid #D0CAC4",
            background: "#FFFFFF",
            color: "#5C5452",
            whiteSpace: "nowrap",
          }}
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M8 3v8M5 8l3 3 3-3" />
            <path d="M3 13h10" />
          </svg>
          Download Template
        </button>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontFamily: "inherit",
            fontSize: 12,
            fontWeight: 600,
            padding: "6px 12px",
            borderRadius: 6,
            cursor: "pointer",
            border: "1.5px solid #f1935c",
            background: "#f1935c",
            color: "#FFFFFF",
            whiteSpace: "nowrap",
            boxShadow: "0 1px 5px rgba(232,115,42,.28)",
          }}
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M8 10V3M5 6l3-3 3 3" />
            <path d="M3 13h10" />
          </svg>
          Upload CSV(s)
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".csv"
          multiple
          style={{ display: "none" }}
          onChange={handleUpload}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        background: "#F6EFE9",
        border: "1px solid #F1C5A5",
        borderRadius: 12,
        padding: "16px 20px",
        marginTop: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 10,
        }}
      >
        <div style={{ minWidth: 240, flex: 1 }}>
          <p
            style={{
              fontWeight: 700,
              fontSize: 13,
              color: "#B45309",
              marginBottom: 4,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            Upload Custom Series
          </p>
          <p style={{ fontSize: 11, color: "#92400E", marginBottom: 0 }}>
            Download the 96-block template, fill the{" "}
            <code
              style={{
                background: "#F4E5D8",
                borderRadius: 4,
                padding: "1px 4px",
              }}
            >
              forecast_mw
            </code>{" "}
            column, then upload. Toggle each uploaded series on/off as an
            overlay.
          </p>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={handleDownloadTemplate}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              border: "1px solid #F1935C",
              borderRadius: 7,
              background: "#FFFFFF",
              color: "#C2440C",
              fontSize: 12,
              fontWeight: 500,
              padding: "6px 12px",
              cursor: "pointer",
            }}
          >
            <span style={{ fontSize: 15 }}>↓</span> Download Template
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              border: "1.5px solid #F1935C",
              borderRadius: 7,
              background: "#F1935C",
              color: "#FFFFFF",
              fontSize: 12,
              fontWeight: 600,
              padding: "6px 12px",
              cursor: "pointer",
            }}
          >
            <span style={{ fontSize: 15 }}>↑</span> Upload CSV(s)
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            multiple
            style={{ display: "none" }}
            onChange={handleUpload}
          />
        </div>
      </div>

      {series.length === 0 ? (
        <p style={{ fontSize: 11, color: "#B45309", margin: 0 }}>
          No uploaded series.{" "}
          <button
            type="button"
            onClick={handleDownloadTemplate}
            style={{
              border: "none",
              background: "none",
              color: "#C2410C",
              textDecoration: "underline",
              cursor: "pointer",
              padding: 0,
              fontSize: "inherit",
            }}
          >
            Download the template
          </button>{" "}
          to start
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {series.map((s) => {
            const hasErrors = s.errors.length > 0;
            const isExpanded = expandedErrors === s.id;
            return (
              <div
                key={s.id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  background: hasErrors ? "#FFF7F7" : "#FFFFFF",
                  border: `1px solid ${hasErrors ? "#FCA5A5" : "#F9C9AF"}`,
                  borderRadius: 8,
                  padding: "8px 12px",
                  gap: 4,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => toggleVisible(s.id)}
                    title={s.visible ? "Hide overlay" : "Show overlay"}
                    style={{
                      width: 32,
                      height: 18,
                      borderRadius: 9,
                      border: "none",
                      background:
                        s.visible && !hasErrors ? "#F1935C" : "#D1D5DB",
                      cursor: hasErrors ? "not-allowed" : "pointer",
                      position: "relative",
                      flexShrink: 0,
                      transition: "background 150ms",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        top: 2,
                        left: s.visible && !hasErrors ? 14 : 2,
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: "#fff",
                        transition: "left 150ms",
                      }}
                    />
                  </button>

                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: "#111827",
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {s.fileName}
                  </span>

                  {s.entity && (
                    <span
                      style={{
                        fontSize: 10,
                        background: "#FDE7D9",
                        color: "#C2440C",
                        borderRadius: 4,
                        padding: "1px 6px",
                        fontWeight: 600,
                      }}
                    >
                      {s.entity}
                    </span>
                  )}
                  {s.date && (
                    <span style={{ fontSize: 10, color: "#92400E" }}>
                      {s.date}
                    </span>
                  )}

                  {hasErrors && (
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedErrors(isExpanded ? null : s.id)
                      }
                      style={{
                        fontSize: 10,
                        color: "#DC2626",
                        background: "#FEE2E2",
                        border: "none",
                        borderRadius: 4,
                        padding: "2px 6px",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                    >
                      {s.errors.length} error{s.errors.length > 1 ? "s" : ""}{" "}
                      {isExpanded ? "▲" : "▼"}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => removeSeries(s.id)}
                    style={{
                      border: "none",
                      background: "none",
                      color: "#F1935C",
                      fontSize: 14,
                      cursor: "pointer",
                      lineHeight: 1,
                      padding: "0 2px",
                    }}
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>

                {isExpanded && (
                  <ul
                    style={{
                      margin: "4px 0 0 40px",
                      padding: 0,
                      listStyle: "disc",
                      fontSize: 11,
                      color: "#B91C1C",
                      maxHeight: 120,
                      overflowY: "auto",
                    }}
                  >
                    {s.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
